import { allocateAcrossMonths, cashImpactOnMonth, projectInstallments, type CashCapItem, type MonthBudget } from './budgetCap'
import type { ProductRow } from '../types'

const MESES_PT = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
/** monthKey YYYY-MM + n meses → YYYY-MM. */
function addMonth(monthKey: string, n: number): string {
  const [y, m] = monthKey.split('-').map(Number)
  const d = new Date(y, m - 1 + n, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function monthLabelPt(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number)
  return `${MESES_PT[m - 1]}/${String(y).slice(-2)}`
}

/**
 * Gera o plano de compra do mês a partir das mesmas linhas do Painel BX,
 * respeitando 3 variáveis: cap mensal, prazos de pagamento e lead time.
 * É determinístico (sem IA) — o Miguel apenas comenta o plano depois.
 *
 * Critérios (mesmos do Painel BX > "Compra urgente / Comprar"):
 *   - priority em [Zerado, Baixo, Comprar]
 *   - sem pedido em aberto (open_qty == 0 OU active_order_number == null)
 *   - não aprovado (kanban_status != Compra Aprovada)
 *   - status não isolado (Bloqueado, Standby, Consignado, Ruptura no Fornecedor)
 *
 * Cap por caixa do mês: usa `splitByCashCap` — itens com prazo longo cabem
 * mesmo quando o cap aperta, porque a 1ª parcela cai no mês seguinte.
 */

const TARGET_PRIORITIES = new Set<string>(['Zerado', 'Baixo', 'Comprar'])
const ISOLATED_STATUSES = new Set<string>(['Bloqueado', 'Standby', 'Consignado', 'Ruptura no Fornecedor', 'Compra Aprovada'])

/**
 * Rank de urgência anti-ruptura (menor = compra primeiro): item já zerado (roxo)
 * vem antes; depois os em risco de zerar (prevenir); por fim o restante. Usado
 * tanto no greedy do cap quanto na ordenação exibida (grupos e linhas).
 */
function urgencyRank(item: { priority: string; em_risco: boolean }): number {
  if (item.priority === 'Zerado') return 0
  if (item.em_risco) return 1
  return 2
}

export interface PlanoItem extends ProductRow {
  /** Custo total da sugestão (qty * cost). */
  total_cost: number
  /** Parcelas projetadas se o pedido fosse emitido hoje. */
  projected_first_due: string | null
  /** Impacto desse item no caixa do mês corrente (R$). 0 se prazos jogam tudo pra meses futuros. */
  impacto_caixa_mes: number
  payment_terms: number[]
  fornecedor_id: string | null
  fornecedor_nome: string
}

export interface PlanoGrupo {
  fornecedor_id: string | null
  fornecedor_nome: string
  lead_time_days: number | null
  min_order: number | null
  rows: PlanoItem[]
  subtotal: number
  impacto_caixa_mes: number
  /** True quando o subtotal supera o min_order ou min_order é null. */
  min_atingido: boolean
}

/** Um mês futuro do cap rolante (julho, agosto...) com sua folga e itens alocados. */
export interface PlanoMesFuturo {
  monthKey: string
  /** Rótulo pt-BR (ex.: "julho/26"). */
  label: string
  /** Folga inicial do mês (teto − parcelas já comprometidas). */
  disponivel: number
  grupos: PlanoGrupo[]
  total: number
}

export interface PlanoDeCompra {
  fits: PlanoGrupo[]
  overflows: PlanoGrupo[]
  totalFits: number
  totalOverflows: number
  /** Soma das parcelas dos itens "fits" que caem no mês corrente. */
  impactoCaixaMes: number
  /** Cap rolante: itens da virada alocados nos próximos meses com folga. */
  mesesFuturos: PlanoMesFuturo[]
  /** Itens que não cabem nem no horizonte (pedem negociação/novo orçamento). */
  naoCabe: PlanoGrupo[]
  /** Lista de avisos determinísticos (cap, dia de corte). */
  alertas: string[]
}

interface Opts {
  rows: ReadonlyArray<ProductRow>
  budgetMonthly: number
  cutoffDay: number
  today: Date
  monthKey: string
  /** Custo real (Custo de Mercadoria, base matriz) por mês (YYYY-MM → R$): corrente, +1, +2.
   *  Base do cap: folga do mês = teto − custo real lançado. Mês sem lançamento ⇒ 0 (teto cheio). */
  costRealByMonth?: Record<string, number>
}

export function gerarPlanoDeCompra(opts: Opts): PlanoDeCompra {
  const { rows, budgetMonthly, cutoffDay, today, monthKey, costRealByMonth = {} } = opts
  const capAtivo = budgetMonthly > 0
  const custoMesAtual = costRealByMonth[monthKey] ?? 0

  // 1. Filtra candidatos.
  // Usa total_suggestion (matriz + filiais) para bater com a coluna "Sugestão Total"
  // do Painel BX. Quando a empresa não tem filial, total_suggestion == purchase_suggestion.
  const candidatos: PlanoItem[] = []
  for (const r of rows) {
    // Anti-ruptura: além de Zerado/Baixo/Comprar, inclui itens em risco de ruptura
    // (ex.: Médio caindo rápido dentro do lead time + colchão) para agir ANTES do roxo.
    if (!TARGET_PRIORITIES.has(r.priority) && !r.em_risco) continue
    if (r.active_order_number) continue
    if (ISOLATED_STATUSES.has(r.kanban_status)) continue
    if ((r.total_suggestion ?? 0) <= 0) continue

    const sup = r.suppliers
    const terms = sup
      ? [sup.payment_term_1, sup.payment_term_2, sup.payment_term_3, sup.payment_term_4, sup.payment_term_5, sup.payment_term_6]
          .filter((t): t is number => typeof t === 'number' && t > 0)
      : []
    const totalCost = r.total_suggestion * (r.cost ?? 0)
    const projected = projectInstallments(totalCost, terms, today)
    const impactoMes = cashImpactOnMonth(projected, monthKey)

    candidatos.push({
      ...r,
      total_cost: totalCost,
      projected_first_due: projected[0]?.due_date ?? null,
      impacto_caixa_mes: impactoMes,
      payment_terms: terms,
      fornecedor_id: r.suppliers?.id ?? null,
      fornecedor_nome: r.suppliers?.name ?? 'Sem fornecedor vinculado',
    })
  }

  // 2. Alocação por CUSTO DE MERCADORIA (cap rolante): o custo total do item conta no
  // mês da compra (payment_terms [] ⇒ projectInstallments joga tudo no mês, sem espalhar
  // por parcelas). Folga de cada mês = teto − custo real já lançado naquele mês.
  const capItems: CashCapItem[] = candidatos.map(it => ({
    id: it.id,
    total_cost: it.total_cost,
    payment_terms: [],
    urgencyRank: urgencyRank(it),
    abcRank: it.abc_curve === 'A' ? 0 : it.abc_curve === 'B' ? 1 : it.abc_curve === 'C' ? 2 : 3,
    score: it.commercial_score ?? 0,
  }))
  const mesesKeys = [monthKey, addMonth(monthKey, 1), addMonth(monthKey, 2)]
  const mesesBudget: MonthBudget[] = mesesKeys.map(mk => ({
    monthKey: mk,
    remaining: capAtivo ? Math.max(0, budgetMonthly - (costRealByMonth[mk] ?? 0)) : Infinity,
  }))
  const alloc = allocateAcrossMonths(capItems, mesesBudget)

  // 3. Mês corrente = fits; o que vai p/ meses seguintes ou não cabe = overflows.
  const currentIds = alloc.assignment.get(monthKey) ?? new Set<string>()
  const fits = candidatos.filter(c => currentIds.has(c.id))
  const overflows = candidatos.filter(c => !currentIds.has(c.id))

  function agrupar(items: PlanoItem[]): PlanoGrupo[] {
    const map = new Map<string, PlanoGrupo>()
    for (const it of items) {
      const key = it.fornecedor_id ?? '__none__'
      const entry = map.get(key) ?? {
        fornecedor_id: it.fornecedor_id,
        fornecedor_nome: it.fornecedor_nome,
        lead_time_days: it.suppliers?.lead_time_days ?? null,
        min_order: it.suppliers?.min_order ?? null,
        rows: [],
        subtotal: 0,
        impacto_caixa_mes: 0,
        min_atingido: false,
      }
      entry.rows.push(it)
      entry.subtotal += it.total_cost
      entry.impacto_caixa_mes += it.impacto_caixa_mes
      map.set(key, entry)
    }
    for (const g of map.values()) {
      g.rows.sort((a, b) =>
        urgencyRank(a) - urgencyRank(b) || (b.commercial_score ?? 0) - (a.commercial_score ?? 0)
      )
      g.min_atingido = g.min_order == null || g.subtotal >= g.min_order
    }
    return Array.from(map.values()).sort((a, b) => {
      const ua = a.rows[0] ? urgencyRank(a.rows[0]) : 99
      const ub = b.rows[0] ? urgencyRank(b.rows[0]) : 99
      return ua - ub
    })
  }

  // 3b. Próximos meses (corrente +1/+2) com a folga de cada um + "não cabe no horizonte".
  const mesesFuturos: PlanoMesFuturo[] = mesesBudget.slice(1).map(mb => {
    const ids = alloc.assignment.get(mb.monthKey) ?? new Set<string>()
    const grupos = agrupar(overflows.filter(o => ids.has(o.id)))
    return {
      monthKey: mb.monthKey,
      label: monthLabelPt(mb.monthKey),
      disponivel: mb.remaining,
      grupos,
      total: grupos.reduce((s, g) => s + g.subtotal, 0),
    }
  })
  const naoCabe = agrupar(overflows.filter(o => alloc.overflowIds.has(o.id)))

  // 4. Alertas determinísticos
  const alertas: string[] = []
  // Anti-ruptura primeiro: destaca quantos itens em risco de zerar entraram no plano.
  const emRiscoFits = fits.filter(f => f.em_risco).length
  if (emRiscoFits > 0) {
    alertas.push(`${emRiscoFits} ${emRiscoFits === 1 ? 'item em risco de ruptura priorizado' : 'itens em risco de ruptura priorizados'} no topo do plano.`)
  }
  const aposCorte = today.getDate() >= cutoffDay
  if (aposCorte) {
    alertas.push(`Hoje é dia ${today.getDate()} — passou do dia de corte (${cutoffDay}). Novas compras devem ir como Standby para a virada.`)
  }
  if (capAtivo) {
    const pct = (custoMesAtual / budgetMonthly) * 100
    if (pct >= 90) alertas.push(`Custo de mercadoria do mês a ${pct.toFixed(0)}% do teto — priorize curva A.`)
    else if (pct >= 70) alertas.push(`Custo de mercadoria do mês a ${pct.toFixed(0)}% do teto.`)
  }
  if (overflows.length > 0 && capAtivo) {
    alertas.push(`${overflows.length} ${overflows.length === 1 ? 'item não cabe' : 'itens não cabem'} no teto deste mês — vão para os próximos meses ou pedem revisão.`)
  }

  const totalFitsCost = fits.reduce((s, f) => s + f.total_cost, 0)
  return {
    fits: agrupar(fits),
    overflows: agrupar(overflows),
    totalFits: totalFitsCost,
    totalOverflows: overflows.reduce((s, o) => s + o.total_cost, 0),
    impactoCaixaMes: totalFitsCost,
    mesesFuturos,
    naoCabe,
    alertas,
  }
}

// ── Fase 3: aprovação por exceção ────────────────────────────────────────────

/** Teto de valor (R$) acima do qual um item sempre vai para revisão humana. */
export const AUTO_APPROVE_MAX_VALUE = 5000
/** Margem % mínima para um item ser auto-aprovável; abaixo disso vira exceção. */
export const AUTO_APPROVE_MIN_MARGIN = 20

export interface AutoApprovalItem {
  item: PlanoItem
  /** Motivos que tornaram o item uma exceção (vazio = seguro). */
  reasons: string[]
}

export interface AutoApprovalPartition {
  seguros: PlanoItem[]
  excecoes: AutoApprovalItem[]
}

/**
 * Classifica os itens do plano (somente `fits`) em SEGUROS — o Miguel aprova sozinho —
 * e EXCEÇÕES — revisão humana — com os motivos. Determinístico; só deve ser usado
 * quando a flag `bx:autoAprovacao` está ligada.
 *
 * Guardrails (todos forçam exceção): valor acima do teto; sem histórico de venda
 * (média ≤ 0); baixa qualidade comercial (curva C/sem curva ou margem abaixo do piso);
 * e pedido mínimo do fornecedor não atingido — este escala o GRUPO inteiro, pois
 * comprar abaixo do mínimo (ou completar) é decisão do comprador.
 */
export function partitionAutoApproval(
  fits: ReadonlyArray<PlanoGrupo>,
  opts: { maxValue?: number; minMargin?: number } = {},
): AutoApprovalPartition {
  const maxValue = opts.maxValue ?? AUTO_APPROVE_MAX_VALUE
  const minMargin = opts.minMargin ?? AUTO_APPROVE_MIN_MARGIN
  const seguros: PlanoItem[] = []
  const excecoes: AutoApprovalItem[] = []

  for (const g of fits) {
    const grupoSemMinimo = !g.min_atingido
    for (const item of g.rows) {
      const reasons: string[] = []
      if (grupoSemMinimo) reasons.push('Pedido mínimo do fornecedor não atingido')
      if (item.total_cost > maxValue) reasons.push(`Valor acima de R$ ${maxValue.toLocaleString('pt-BR')}`)
      if ((item.monthly_avg ?? 0) <= 0) reasons.push('Sem histórico de venda')
      if (item.abc_curve === 'C' || item.abc_curve == null) reasons.push('Curva C/sem curva')
      if ((item.margin_pct ?? 0) < minMargin) reasons.push(`Margem abaixo de ${minMargin}%`)

      if (reasons.length === 0) seguros.push(item)
      else excecoes.push({ item, reasons })
    }
  }

  return { seguros, excecoes }
}
